import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, PanelLeftClose, PanelLeft, LayoutGrid } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import useTranslation from "../../hooks/useTranslation";
import { trackNavigation } from "../../utils/posthog";
import { cn } from "../../utils";
import { useQuery, getMyStudents } from "wasp/client/operations";
import {
  getModules,
  moduleForPath,
  buildCustomPagesGroup,
  buildStudentEntries,
  studentIdFromPath,
  NO_STUDENT_PERMISSIONS,
  rememberTab,
  type SecondaryEntry,
  type SecondaryGroup,
} from "./navConfig";

/**
 * Level 2: the light contextual sidebar, driven entirely by navConfig.
 *
 * Renders nothing for modules that have no entries (mentor, propfirm in V1) so those
 * pages get the full width.
 */

const SIDEBAR_WIDTH = 180;
/** Wide enough for a 32px icon button plus breathing room on each side. */
const COLLAPSED_WIDTH = 48;
const COLLAPSE_KEY = "edgely:nav:secondaryCollapsed";

/** Same easing as the legacy AppSidebar, so both rails feel identical. */
const WIDTH_TRANSITION = "width 220ms cubic-bezier(0.22, 1, 0.36, 1)";


function EntryLink({
  entry,
  isActive,
  onNavigate,
  indented = false,
}: {
  entry: SecondaryEntry;
  isActive: boolean;
  onNavigate: (to: string) => void;
  indented?: boolean;
}) {
  const Icon = entry.icon;

  return (
    <button
      type="button"
      onClick={() => onNavigate(entry.to)}
      aria-current={isActive ? "page" : undefined}
      className={cn(
        "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors",
        indented && "pl-7",
        isActive
          ? "bg-primary font-medium text-primary-foreground"
          : "text-foreground/75 hover:bg-accent hover:text-accent-foreground"
      )}
    >
      {Icon && <Icon className="h-4 w-4 shrink-0" />}
      <span className="truncate">{entry.label}</span>
    </button>
  );
}

function CollapsibleGroup({
  group,
  pathname,
  onNavigate,
  addPageSlot,
}: {
  group: SecondaryGroup;
  pathname: string;
  onNavigate: (to: string) => void;
  /** Rendered as the last row of the group — only the custom-pages group gets one. */
  addPageSlot?: React.ReactNode;
}) {
  const [open, setOpen] = useState(group.defaultOpen ?? false);
  const Icon = group.icon;

  return (
    <li>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-foreground/60 transition-colors hover:bg-accent hover:text-accent-foreground"
      >
        {Icon && <Icon className="h-4 w-4 shrink-0" />}
        <span className="flex-1 truncate">{group.label}</span>
        <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <ul className="mt-0.5 space-y-0.5">
          {group.entries.map((entry) => (
            <li key={entry.id}>
              <EntryLink
                entry={entry}
                isActive={pathname === entry.to}
                onNavigate={onNavigate}
                indented
              />
            </li>
          ))}
          {addPageSlot && <li className="pl-5">{addPageSlot}</li>}
        </ul>
      )}
    </li>
  );
}

export default function SecondarySidebar({
  customPages = [],
  addPageSlot,
  collapsedAddPageSlot,
}: {
  customPages?: Array<{ id: string; name: string; icon?: string }>;
  /** "Add a page" control, owned by the caller so it can carry its own modals. */
  addPageSlot?: React.ReactNode;
  /** Same control in its icon-only variant, for the collapsed strip. A second
   *  slot rather than one reused node: both states can be mounted across a
   *  fold, and a single element would have to move between two trees. */
  collapsedAddPageSlot?: React.ReactNode;
}) {
  const t = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // The persisted flag now means "rail mode" rather than "always narrow": in rail
  // mode the sidebar expands on hover and folds back on leave, so the page keeps
  // the full width the rest of the time (same model as the legacy AppSidebar).
  const [railMode, setRailMode] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === "true";
    } catch {
      return false;
    }
  });
  const [isHovered, setIsHovered] = useState(false);
  // Set when folding: the cursor is still over the sidebar at that moment, and
  // the pointer events that follow the re-render would re-expand it immediately.
  // Cleared on mouse leave, so hover works again from the next pass onwards.
  const [hoverSuppressed, setHoverSuppressed] = useState(false);

  const collapsed = railMode && !(isHovered && !hoverSuppressed);

  const modules = getModules(t);
  const activeModuleId = moduleForPath(location.pathname, modules);
  const activeModule = modules.find((m) => m.id === activeModuleId);

  // Only fetched while a student is open: every other page would pay for a
  // mentor query it never reads. `enabled` keeps non-mentors from calling it at all.
  const openStudentId = studentIdFromPath(location.pathname);
  const { data: mentorStudents = [] } = useQuery(
    getMyStudents,
    undefined,
    { enabled: openStudentId != null }
  );

  // Remember where the trader was inside this module, and log the move so the
  // time-to-reach of the most-visited pages can be measured after release.
  useEffect(() => {
    if (!activeModule) return;
    rememberTab(activeModule.id, location.pathname);

    const entry =
      activeModule.entries?.find((e) => e.to === location.pathname) ??
      activeModule.groups?.flatMap((g) => g.entries).find((e) => e.to === location.pathname);

    trackNavigation({
      module: activeModule.id,
      tab: entry?.id ?? "unknown",
      path: location.pathname,
    });
  }, [activeModule, location.pathname]);

  function toggleCollapsed() {
    setRailMode((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSE_KEY, String(next));
      } catch {
        // Storage unavailable — the toggle still works for this session.
      }
      // Folding leaves the cursor over the sidebar, which would instantly
      // re-expand it via hover and make the click look like a no-op.
      if (next) setHoverSuppressed(true);
      return next;
    });
  }

  // The student's own pages are appended to the mentor module while one is open,
  // replacing the horizontal tab bar StudentLayout used to render.
  const openStudent = openStudentId
    ? (mentorStudents as any[]).find((s) => s.studentId === openStudentId)
    : undefined;

  const entries: SecondaryEntry[] = [
    ...(activeModule?.entries ?? []),
    ...(activeModule?.id === "mentor" && openStudentId && openStudent
      ? buildStudentEntries(t, openStudentId, openStudent.permissions ?? NO_STUDENT_PERMISSIONS)
      : []),
  ];

  // Modules without entries render full width (propfirm in V1).
  if (!activeModule || entries.length === 0) return null;

  const groups: SecondaryGroup[] = [];
  if (activeModule.id === "dashboard") {
    groups.push(buildCustomPagesGroup(t, customPages));
  }
  if (activeModule.groups) groups.push(...activeModule.groups);

  // Student sub-pages nest (…/session/:id lives under …/sessions), so an exact
  // match would leave nothing highlighted there. Entries that are a prefix of
  // their own siblings are matched exactly instead: "/mentor" precedes every
  // "/mentor/student/…" path, and the student root precedes all its children —
  // prefix-matching either would light up two entries at once.
  const exactOnly = new Set(
    ["/mentor", openStudentId ? `/mentor/student/${openStudentId}` : null].filter(
      (p): p is string => p != null
    )
  );
  const isEntryActive = (to: string) =>
    exactOnly.has(to)
      ? location.pathname === to
      : location.pathname === to || location.pathname.startsWith(`${to}/`);

  // Flattened for the collapsed strip: group entries are included even though their
  // group is collapsible, since there is no way to expand a group at 48px wide.
  const collapsedEntries: Array<SecondaryEntry & { groupId?: string }> = [
    ...entries,
    ...groups.flatMap((g) => g.entries.map((e) => ({ ...e, groupId: g.id }))),
  ];

  // In rail mode the slot keeps the narrow width and the panel expands OVER the
  // page. Letting the flow width follow the hover would reflow every widget each
  // time the cursor crosses the sidebar. Pinned open, slot and panel match, so the
  // content is laid out beside it exactly as before.
  const slotWidth = railMode ? COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  // One persistent <aside> whose width eases between the two states. Returning a
  // different element per state (as this did) swaps one DOM node for another, so
  // there is nothing for the browser to interpolate and the rail jumps.
  return (
    <div
      // The slot owns the sticky positioning and the width the page lays out
      // against; the panel inside is absolute so widening it displaces nothing.
      // `sticky` already establishes the containing block the absolute panel
      // positions against, so no `relative` here.
      //
      // While overlaying it must outrank the page: widgets create their own
      // stacking contexts (relative z-10 / z-20 / z-30) and, being later in DOM
      // order, would otherwise paint straight over the panel's labels.
      className={cn(
        "hidden lg:block sticky top-0 h-screen shrink-0",
        railMode && !collapsed ? "z-50" : "z-30"
      )}
      style={{ width: slotWidth, transition: WIDTH_TRANSITION }}
    >
      <aside
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => {
          setIsHovered(false);
          setHoverSuppressed(false);
        }}
        className={cn(
          "absolute left-0 top-0 flex h-screen flex-col overflow-hidden border-r border-border bg-card",
          collapsed && "items-center py-3",
          // Lifted off the page only while it overlays.
          railMode && !collapsed && "shadow-xl"
        )}
        style={{ width: collapsed ? COLLAPSED_WIDTH : SIDEBAR_WIDTH, transition: WIDTH_TRANSITION }}
        aria-label={activeModule.label}
      >
        {collapsed ? (
          /* Collapsed still exposes every destination as an icon. Showing only the
             expand button made the strip a dead end: Journal, Analytics and the rest
             became unreachable without expanding first. */
          <TooltipProvider delayDuration={200}>
            <button
              type="button"
              onClick={toggleCollapsed}
              aria-label={t.newNav.expand}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              <PanelLeft className="h-4 w-4" />
            </button>

            <nav aria-label={activeModule.label} className="mt-2 flex flex-col items-center gap-1 overflow-y-auto overflow-x-hidden">
              {collapsedEntries.map((entry) => {
                // Custom pages carry no icon through buildCustomPagesGroup, so they
                // would render an empty button without a fallback.
                const Icon = entry.icon ?? LayoutGrid;
                const isActive = isEntryActive(entry.to);

                return (
                  <Tooltip key={`${entry.groupId ?? "root"}-${entry.id}`}>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() => navigate(entry.to)}
                        aria-label={entry.label}
                        aria-current={isActive ? "page" : undefined}
                        className={cn(
                          "flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors",
                          isActive
                            ? "bg-primary text-primary-foreground"
                            : "text-foreground/70 hover:bg-accent hover:text-accent-foreground"
                        )}
                      >
                        <Icon className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="whitespace-nowrap">
                      {entry.label}
                    </TooltipContent>
                  </Tooltip>
                );
              })}

              {/* Conditionne au groupe des pages perso, comme la ligne d'ajout
                  de l'etat deploye : les autres modules n'ont rien a ajouter. */}
              {collapsedAddPageSlot &&
                groups.some((g) => g.id === "custom-pages") &&
                collapsedAddPageSlot}
            </nav>
          </TooltipProvider>
        ) : (
          <>
            <div className="flex items-center justify-between px-3 py-3">
              {/* The label is fixed to the open width: left to size itself it would
                  reflow character by character as the box shrinks. */}
              <span
                className="truncate text-sm font-semibold"
                style={{ width: SIDEBAR_WIDTH - 56 }}
              >
                {activeModule.label}
              </span>
              <button
                type="button"
                onClick={toggleCollapsed}
                aria-label={t.newNav.collapse}
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
              >
                <PanelLeftClose className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* Pinned to the open width for the same reason: the nav must slide out
                with the box rather than re-wrap its labels while it animates. */}
            <nav
              className="flex-1 overflow-y-auto overflow-x-hidden px-2 pb-3"
              style={{ width: SIDEBAR_WIDTH }}
            >
              <ul className="space-y-0.5">
                {entries.map((entry) => (
                  <li key={entry.id}>
                    <EntryLink
                      entry={entry}
                      isActive={isEntryActive(entry.to)}
                      onNavigate={navigate}
                    />
                  </li>
                ))}

                {groups.map((group) => (
                  <CollapsibleGroup
                    key={group.id}
                    group={group}
                    pathname={location.pathname}
                    onNavigate={navigate}
                    addPageSlot={group.id === "custom-pages" ? addPageSlot : undefined}
                  />
                ))}
              </ul>
            </nav>
          </>
        )}
      </aside>
    </div>
  );
}
